import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { FaExternalLinkAlt, FaGithub } from 'react-icons/fa';
import { SiAppstore } from 'react-icons/si';

type Project = {
  title: string;
  role: string;
  stackLine: string;
  description: string;
  highlights: string[];
  tech: string[];
  github: string;
  live?: string | null;
  appStore?: string | null;
  image: string;
};

const ProjectsSection = styled.section`
  padding: 110px 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.3), transparent);
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2.35rem, 5vw, 3.6rem);
  font-weight: 900;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #ffffff 0%, #9beeff 56%, #7affc8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -0.7rem;
    left: 0;
    width: 88px;
    height: 3px;
    background: linear-gradient(90deg, #7affc8, rgba(103, 216, 255, 0));
  }
  
  @media (max-width: 768px) {
    font-size: 2.25rem;
  }
`;

const SectionHeader = styled.div`
  max-width: 720px;
  margin-bottom: 3.25rem;
`;

const SectionLead = styled(motion.p)`
  color: #aebdcb;
  font-size: 1.02rem;
  line-height: 1.8;
  margin-top: 1.75rem;
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.4rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ProjectSummary = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 2.25rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryItem = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 8px;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.048);
`;

const SummaryValue = styled.strong`
  display: block;
  color: #ffffff;
  font-size: 1.2rem;
  line-height: 1.25;
`;

const SummaryLabel = styled.span`
  color: #8fa2b5;
  font-size: 0.84rem;
`;

const ProjectCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.095);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  backdrop-filter: blur(15px);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #7affc8, #67d8ff);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
    box-shadow: 0 0 20px rgba(0, 212, 255, 0.6);
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(128, 230, 255, 0.34);
    box-shadow: 0 22px 48px rgba(0, 0, 0, 0.22);
    
    &::before {
      transform: scaleX(1);
    }
    
  }
`;

const ProjectImage = styled(motion.div)`
  width: 100%;
  height: 132px;
  background: linear-gradient(135deg, rgba(122, 255, 200, 0.13), rgba(103, 216, 255, 0.1));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3.2rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
    background-size: 24px 24px;
  }
  
  &::after {
    content: '';
    position: absolute;
    inset: auto 1.4rem 1.2rem auto;
    width: 68px;
    height: 4px;
    border-radius: 999px;
    background: linear-gradient(90deg, #7affc8, #67d8ff);
  }
`;

const ProjectContent = styled.div`
  padding: 1.45rem;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ProjectMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-bottom: 0.95rem;
`;

const MetaPill = styled.span`
  color: #dbe8f3;
  font-size: 0.75rem;
  font-weight: 800;
  line-height: 1;
  padding: 0.42rem 0.62rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.055);
`;

const ProjectTitle = styled.h3`
  font-size: 1.12rem;
  color: #ffffff;
  margin-bottom: 0.8rem;
  font-weight: 700;
`;

const StackLine = styled.p`
  color: #7affc8;
  font-size: 0.84rem;
  font-weight: 800;
  margin: -0.35rem 0 0.85rem;
`;

const ProjectDescription = styled.p`
  color: #aebdcb;
  line-height: 1.72;
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  font-weight: 400;
`;

const HighlightList = styled.ul`
  list-style: none;
  display: grid;
  gap: 0.55rem;
  margin-bottom: 1.35rem;
`;

const HighlightItem = styled.li`
  color: #d3dfeb;
  font-size: 0.88rem;
  line-height: 1.58;
  display: grid;
  grid-template-columns: 14px 1fr;
  gap: 0.55rem;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    margin-top: 0.55rem;
    border-radius: 999px;
    background: #7affc8;
    box-shadow: 0 0 12px rgba(122, 255, 200, 0.55);
  }
`;

const ProjectTech = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 1.5rem;
`;

const TechTag = styled(motion.span)`
  background: rgba(128, 230, 255, 0.08);
  color: #9beeff;
  padding: 0.36rem 0.72rem;
  border-radius: 999px;
  font-size: 0.76rem;
  border: 1px solid rgba(128, 230, 255, 0.17);
  font-weight: 700;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.3), transparent);
    transform: translateX(-100%);
    transition: transform 0.5s ease;
  }
  
  &:hover {
    background: rgba(128, 230, 255, 0.16);
    border-color: rgba(128, 230, 255, 0.45);
    
    &::before {
      transform: translateX(100%);
    }
  }
`;

const ProjectLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const ProjectLink = styled(motion.a)`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: #9beeff;
  font-size: 0.9rem;
  font-weight: 800;
  transition: all 0.3s ease;
  
  &:hover {
    color: #ffffff;
  }
`;

const ProjectCard3D: React.FC<{ project: Project; index: number }> = ({ project, index }) => {
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  
  const mouseXSpring = useSpring(x, { damping: 25, stiffness: 200 });
  const mouseYSpring = useSpring(y, { damping: 25, stiffness: 200 });
  
  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ['7.5deg', '-7.5deg']);
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ['-7.5deg', '7.5deg']);
  
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    
    const rect = ref.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const xPct = mouseX / width - 0.5;
    const yPct = mouseY / height - 0.5;
    
    x.set(xPct);
    y.set(yPct);
  };
  
  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <ProjectCard
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      viewport={{ once: true }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        rotateX,
        rotateY,
        transformStyle: 'preserve-3d',
      }}
      whileHover={{ scale: 1.02 }}
    >
      <ProjectImage
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.3 }}
      >
        {project.image}
      </ProjectImage>
      <ProjectContent>
        <ProjectMeta>
          <MetaPill>{project.role}</MetaPill>
        </ProjectMeta>
        <ProjectTitle>{project.title}</ProjectTitle>
        <StackLine>{project.stackLine}</StackLine>
        <ProjectDescription>{project.description}</ProjectDescription>
        <HighlightList>
          {project.highlights.map((highlight) => (
            <HighlightItem key={highlight}>{highlight}</HighlightItem>
          ))}
        </HighlightList>
        <ProjectTech>
          {project.tech.map((tech: string, techIndex: number) => (
            <TechTag 
              key={techIndex}
              initial={{ opacity: 0, scale: 0 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: techIndex * 0.05 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.15, y: -3 }}
            >
              {tech}
            </TechTag>
          ))}
        </ProjectTech>
        <ProjectLinks>
          <ProjectLink 
            href={project.github} 
            target="_blank" 
            rel="noopener noreferrer"
            whileHover={{ x: 5, scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaGithub />
            Code
          </ProjectLink>
          {project.live && (
            <ProjectLink 
              href={project.live} 
              target="_blank" 
              rel="noopener noreferrer"
              whileHover={{ x: 5, scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaExternalLinkAlt />
              {project.appStore ? 'Web' : 'Live Demo'}
            </ProjectLink>
          )}
          {project.appStore && (
            <ProjectLink 
              href={project.appStore} 
              target="_blank" 
              rel="noopener noreferrer"
              whileHover={{ x: 5, scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <SiAppstore />
              App Store
            </ProjectLink>
          )}
        </ProjectLinks>
      </ProjectContent>
    </ProjectCard>
  );
};

const Projects: React.FC = () => {
  const projects: Project[] = [
    {
      title: '1on-Musk 캡스톤 디자인 (팀 프로젝트)',
      role: 'Backend · Realtime · AI',
      stackLine: 'Java 21 · Spring Boot 3.2 · PostgreSQL · STOMP · OpenAI',
      description: '실시간 협업 브레인스토밍 플랫폼에서 백엔드 개발을 맡아 워크스페이스 API, 실시간 채팅/파일 업로드, 초대·권한 플로우를 구현했습니다. 이후 OpenAI PRD/프로토타입 생성 파이프라인과 배포 자동화까지 확장하며 서비스의 핵심 흐름을 직접 설계했습니다.',
      highlights: [
        '워크스페이스 생성·목록·상세·이름 변경·삭제 API와 멤버십 기반 필터링, 나가기/초대 수락 권한 로직 구현',
        '채팅 REST API, 실시간 메시지 전파, 파일 업로드와 소켓 메타데이터 전송, STOMP WebSocket 전환 및 테스트 안정화',
        'OpenAI PRD 생성, React 프로토타입 생성, Vercel 직접 배포, PRD 미리보기 API까지 이어지는 AI 프로토타입 파이프라인 구축',
        '썸네일 업로드·자동 생성·내용 기반 갱신, 휴지통 soft delete/복원/영구 삭제, CI/CD·OAuth·CORS 환경 설정 보강'
      ],
      tech: ['Java 21', 'Spring Boot 3.2', 'PostgreSQL', 'STOMP', 'OpenAI', 'OAuth', 'JWT', 'Swagger'],
      github: 'https://github.com/1on-Musk-Capstone-Design/backend',
      live: 'https://on-it.kro.kr',
      appStore: 'https://apps.apple.com/kr/app/on-it/id6763914054',
      image: '🧠'
    },
    {
      title: 'K-Pass (팀 프로젝트)',
      role: 'Mobile Frontend · Map UX',
      stackLine: 'Flutter · Dart · provider · flutter_map · geolocator',
      description: 'Figma 디자인을 Flutter 화면으로 옮기며 위치 기반 지도와 저장 위치 리스트를 실제 사용 흐름에 맞게 구현했습니다. 스플래시, 로그인, 홈, 지도, 알림 화면을 연결하고 API service 계층을 분리해 앱 구조를 정리했습니다.',
      highlights: [
        'OpenStreetMap 타일과 마커, 하단 DraggableScrollableSheet 리스트를 양방향 동기화',
        '현재 위치 권한, 초기 위치 리셋, 즐겨찾기 토글 등 지도 조작 플로우 구현',
        'auth, community, drain, notification 등 서비스 계층을 분리해 화면과 API 호출을 정리'
      ],
      tech: ['Flutter', 'Dart', 'provider', 'flutter_map', 'geolocator', 'OpenStreetMap', 'API Service'],
      github: 'https://github.com/K-PaaS-in-Future/frontend',
      live: null,
      image: '📍'
    },
    {
      title: 'Lucky B-Key (팀 프로젝트)',
      role: 'Backend · Domain API · AI',
      stackLine: 'Java 17 · Spring Boot 3.2 · MariaDB · JWT · AWS S3 · OpenAI',
      description: '폭염 쉼터 추천 및 운영 관리 REST API의 백엔드 개발을 담당했습니다. 쉼터 추천 도메인을 시작으로 쿠폰, 공지사항, AI 추천/혼잡도 예측, QR 체크인, S3 업로드와 보안 설정까지 서비스 운영에 필요한 기능을 직접 구현했습니다.',
      highlights: [
        '쉼터 CRUD, 거리 기반 추천, 혼잡도 예측, 사용자 정보 API와 JWT 인증을 포함한 초기 백엔드 골격 구축',
        '쿠폰 생성·조회·발급·사용, 사용자 보유 쿠폰 관리와 예외 처리, 공지사항 CRUD API 구현',
        'OpenAI 기반 쉼터 추천·혼잡도 예측, 맞춤 알림 추천, AI 맞춤 광고 API와 기능 문서 정리',
        'QR 코드 생성·디코드·저장 API, 단위/통합 테스트, S3 이미지 업로드 설정, MariaDB/MySQL 전환 기록 보강'
      ],
      tech: ['Spring Boot', 'Java 17', 'MariaDB', 'JWT', 'AWS S3', 'OpenAI', 'QR Code', 'JPA'],
      github: 'https://github.com/LuckyB-key/Back-End',
      live: null,
      image: '🏠'
    },
    {
      title: 'KMU 장학 챗봇',
      role: 'AI Chatbot · Knowledge Base',
      stackLine: 'Python · Flask · OpenAI · scikit-learn · pandas',
      description: '계명대학교 장학금 질의를 빠르게 찾을 수 있도록 만든 챗봇 프로젝트입니다. JSON 기반 지식 베이스로 FAQ를 먼저 매칭하고, 답변이 부족한 질문은 OpenAI 응답으로 보완하는 흐름을 구성했습니다.',
      highlights: [
        '정확 일치와 단어 교집합 기반 후보 검색 후 미매칭 질문은 GPT로 fallback',
        '학습 명령으로 질문·답변을 JSON 지식 베이스에 추가하고 재사용',
        'Flask, pandas, scikit-learn 의존성을 갖춘 웹 챗봇 확장 기반 마련'
      ],
      tech: ['Python', 'Flask', 'OpenAI API', 'JSON KB', 'scikit-learn', 'pandas'],
      github: 'https://github.com/qlsl1198/kmu_janghak',
      live: 'https://kmu-janghak.onrender.com/',
      image: '🎓'
    },
    {
      title: 'Movie Explorer',
      role: 'Vanilla JS · Search UI',
      stackLine: 'JavaScript · HTML · CSS · Movie API',
      description: '영화 탐색용 웹 인터페이스입니다. 프론트 코드에서 검색어, 장르 체크박스, 국가, 정렬, 개봉 상태를 URLSearchParams로 조합해 API에 요청하고 결과를 카드/모달로 렌더링합니다.',
      highlights: [
        '검색·장르·국가·정렬·개봉 상태 필터와 페이지네이션 상태를 직접 관리',
        '상세 모달에서 감독, 출연진, 포스터 fallback, 한국어 날짜 포맷 처리',
        '이미지 lazy loading과 에러 fallback으로 영화 카드 표시 안정성 개선'
      ],
      tech: ['JavaScript', 'HTML', 'CSS', 'Fetch API', 'Pagination', 'Modal UI'],
      github: 'https://github.com/qlsl1198/movie-explorer',
      live: 'https://movie-explorer-jdy5.onrender.com/',
      image: '🎬'
    },
    {
      title: 'Fortune Personality Test',
      role: 'React · Interactive Content',
      stackLine: 'React 18 · Vite · React Router · Kakao AdFit',
      description: '성격 테스트, 오늘의 운세, 타로 카드 플로우를 라우트 단위로 나눈 React 웹앱입니다. Vite 기반으로 구성되어 있고 각 화면에 Kakao AdFit 배너 컴포넌트를 분리 적용했습니다.',
      highlights: [
        'React Router로 테스트, 결과, 운세, 타로 화면을 독립 라우트로 구성',
        '광고 스크립트 로딩과 언마운트 정리를 컴포넌트 생명주기에 맞춰 처리',
        '짧은 콘텐츠 소비 흐름에 맞춰 CTA, 통계, 기능 카드 중심 홈 화면 구성'
      ],
      tech: ['React', 'Vite', 'React Router', 'JavaScript', 'Kakao AdFit', 'CSS'],
      github: 'https://github.com/qlsl1198/fortune',
      live: 'https://fortune-henna.vercel.app/',
      image: '🔮'
    },
    {
      title: '자산 관리 챗봇',
      role: 'PHP · AI Counseling Flow',
      stackLine: 'PHP · JSON Storage · Prompt Design · Form Flow',
      description: '개인 프로필과 지출 입력을 바탕으로 AI 상담/분석 결과를 제공하는 PHP 웹앱입니다. 입력, 처리, 결과 화면을 분리하고 사용자 데이터와 상담 로그를 JSON으로 저장하는 구조로 구현했습니다.',
      highlights: [
        '프로필 입력, 지출 입력, AI 상담 채팅, 분석 결과까지 단계형 화면 구성',
        'system_prompt와 사용자 로그를 분리해 상담 톤과 대화 기록을 관리',
        '정적 이미지·프로필 에셋을 포함해 해커톤 데모에 맞춘 빠른 배포 구조 구현'
      ],
      tech: ['PHP', 'JSON', 'Prompt Engineering', 'Web Form', 'Chat UI'],
      github: 'https://github.com/qlsl1198/hackathon',
      live: null,
      image: '💰'
    },
    {
      title: 'AI Personal Assistant',
      role: 'AI App · Full Stack Prototype',
      stackLine: 'React Native · Expo · TypeScript · Express · MongoDB · OpenAI',
      description: '모바일/웹 겸용 AI 개인 비서 앱과 백엔드 API를 함께 구성했습니다. GPT 채팅, Whisper 음성 인식, Vision 이미지 분석, OAuth 로그인, SQLite 저장 흐름을 화면 단위로 나누고 JWT·MongoDB 기반 API 골격을 설계했습니다.',
      highlights: [
        '채팅, 음성 녹음, 이미지 분석, 설정 화면을 React Native/Expo 화면 단위로 구성',
        'Google·Kakao OAuth와 OpenAI API 키 저장 흐름을 앱 설정에 통합',
        'Express TypeScript 백엔드에 auth, chat, voice, image, user route와 에러 미들웨어 구성'
      ],
      tech: ['React Native', 'Expo', 'TypeScript', 'OpenAI', 'Whisper', 'Vision', 'Express', 'MongoDB'],
      github: 'https://github.com/qlsl1198/ai_vacation',
      live: null,
      image: '🤖'
    },
    {
      title: '스터디 파트너',
      role: 'Full Stack · Matching Platform',
      stackLine: 'Flutter Web · Spring Boot 2.7 · H2 · JWT · OAuth',
      description: '스터디 생성, 검색, 참여, 알림을 다루는 웹 기반 매칭 플랫폼입니다. Flutter Web 화면과 Spring Boot 백엔드를 함께 구성하고 Study, User, Notification, Post 도메인으로 기능을 분리했습니다.',
      highlights: [
        'Spring Security, JWT filter, OAuth service로 로그인과 보호된 API 접근 흐름 구성',
        'Study, StudyMember, StudyPost, Notification 엔티티와 서비스 계층으로 도메인 분리',
        'Flutter Web에서 GoRouter, Dio, Material Design 3 기반 화면 흐름 구현'
      ],
      tech: ['Flutter Web', 'Dart', 'Spring Boot', 'Java', 'JWT', 'OAuth', 'H2', 'JPA'],
      github: 'https://github.com/qlsl1198/likelion_side',
      live: null,
      image: '📚'
    },
    {
      title: 'Dream Interpreter App',
      role: 'React Native · Product App',
      stackLine: 'React Native · TypeScript · Expo · AsyncStorage · Notifications',
      description: 'AI 꿈 해석, 기억 복원, 히스토리, 통계, 설정, 후원/광고까지 포함한 모바일 앱입니다. 서비스 계층에 DreamStorage, Network, Backup, Notification, Feedback 로직을 분리했습니다.',
      highlights: [
        '꿈 기록을 로컬 저장하고 히스토리·통계 화면에서 패턴과 감정 추이를 확인',
        '네트워크 상태에 따른 OpenAI/기본 해석 fallback과 백업·복원 흐름 구성',
        'Expo Notifications, EAS Build, AdFit/AdMob, 후원 모달까지 출시형 앱 기능 구현'
      ],
      tech: ['React Native', 'TypeScript', 'Expo', 'OpenAI', 'AsyncStorage', 'Notifications', 'EAS Build'],
      github: 'https://github.com/qlsl1198/Dream',
      live: null,
      image: '🌙'
    }
  ];

  return (
    <ProjectsSection id="projects">
      <SectionHeader>
        <SectionTitle
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Selected Projects
        </SectionTitle>
        <SectionLead
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
        >
          협업 플랫폼, 위치 기반 앱, AI 챗봇처럼 실제 사용 흐름이 있는 프로젝트를 중심으로 정리했습니다.
          제가 맡은 구현 범위와 기술 선택이 바로 보이도록 프로젝트별 역할, 핵심 기능, 사용 기술을 함께 정리했습니다.
        </SectionLead>
      </SectionHeader>

      <ProjectSummary
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.15 }}
        viewport={{ once: true }}
      >
        <SummaryItem>
          <SummaryValue>4 Backend APIs</SummaryValue>
          <SummaryLabel>Spring/Express/PHP 기반 서버와 도메인 API 경험</SummaryLabel>
        </SummaryItem>
        <SummaryItem>
          <SummaryValue>4 Mobile/Web Apps</SummaryValue>
          <SummaryLabel>Flutter, React Native, React/Vite 화면 구현</SummaryLabel>
        </SummaryItem>
        <SummaryItem>
          <SummaryValue>AI + Realtime</SummaryValue>
          <SummaryLabel>OpenAI, STOMP WebSocket, OAuth, 지도/위치 기능</SummaryLabel>
        </SummaryItem>
      </ProjectSummary>
      
      <ProjectsGrid>
        {projects.map((project, index) => (
          <ProjectCard3D key={index} project={project} index={index} />
        ))}
      </ProjectsGrid>
    </ProjectsSection>
  );
};

export default Projects; 
